import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { createPortal } from 'react-dom';

export type QuickReportTooltipProps = {
  date: string;
  dayLabel?: string;
  progress: {
    label: string;
    pct: number;
  };
  rows: {
    label: string;
    value: string;
  }[];
};

export function QuickReportTooltip({ date, dayLabel, progress, rows }: QuickReportTooltipProps) {
  return (
    <div className="quickReportTooltip">
      <div className="quickReportTooltip__header">
        <span className="quickReportTooltip__date">{date}</span>
        {dayLabel ? <span className="quickReportTooltip__day">{dayLabel}</span> : null}
      </div>
      <div className="quickReportTooltip__divider" />
      <div className="quickReportTooltip__rows">
        {rows.map(({ label, value }, index) => (
          <div className="quickReportTooltip__row" key={label}>
            <span className={`quickReportTooltip__dot quickReportTooltip__dot--${index === 0 ? 'day' : 'total'}`} />
            <span className="quickReportTooltip__rowLabel">{label}</span>
            <span className="quickReportTooltip__rowValue">{value}</span>
          </div>
        ))}
      </div>
      <div className="quickReportTooltip__progress">
        <div className="quickReportTooltip__progressTrack">
          <div className="quickReportTooltip__progressFill" style={{ width: `${Math.min(progress.pct, 100)}%` }} />
        </div>
        <span className="quickReportTooltip__progressLabel">{progress.label}</span>
      </div>
    </div>
  );
}

export type TooltipAnchor = { index: number; x: number; y: number };

export type QuickReportTooltipPortalHandle = {
  hide: () => void;
  show: (anchor: TooltipAnchor) => void;
};

// Owns the hover state so hovering doesn't re-render (and redraw) the chart, and renders into the body so no
// list or expanded-row container clips it.
export const QuickReportTooltipPortal = forwardRef<
  QuickReportTooltipPortalHandle,
  { getProps: (index: number) => QuickReportTooltipProps }
>(({ getProps }, ref) => {
  const [anchor, setAnchor] = useState<TooltipAnchor | null>(null);

  useImperativeHandle(ref, () => ({ hide: () => setAnchor(null), show: setAnchor }), []);

  useEffect(() => {
    if (!anchor) {
      return undefined;
    }
    // The fixed position goes stale as soon as the page or the list scrolls
    const hide = () => setAnchor(null);
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [anchor]);

  return anchor
    ? createPortal(
        <div className="quickReportTooltipPortal" style={{ left: anchor.x, top: anchor.y }}>
          <QuickReportTooltip {...getProps(anchor.index)} />
        </div>,
        document.body
      )
    : null;
});
